import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader/PageHeader';
import useMembershipPlanService from '../../app/services/hooks/useMembershipPlanService';
import './MySubscription.scss';
import { safeRedirectUrl } from '../Organization/types';

interface MembershipPlan {
    id: number;
    name: string;
    code: string;
    description: string;
    short_description: string;
    price: number;
    price_formatted: string;
    iva: number;
    iva_formatted: string;
    price_with_iva: number;
    price_with_iva_formatted: string;
    currency: string;
    duration_days: number;
    duration_display: string;
    contracts_included: number;
    contracts_display: string;
    is_unlimited_contracts: boolean;
    chatbot_enabled: boolean;
    chatbot_messages_limit: number;
    is_chatbot_unlimited: boolean;
    features: string[];
    exclusions: string[];
    is_featured: boolean;
    badge_text: string;
}

interface UserMembership {
    id: number;
    plan: MembershipPlan;
    start_date: string;
    end_date: string;
    days_remaining: number;
    contracts_used: number;
    contracts_remaining: number;
    can_use_contract: boolean;
    chatbot_messages_used: number;
    chatbot_messages_remaining: number;
    can_use_chatbot: boolean;
    is_valid: boolean;
    payment_status: string;
    is_subscription: boolean;
    subscription_status: string;
    next_invoice_date: string;
}

const breadcrumbs = [
    { label: 'Inicio', url: '/intranet' },
    { label: 'Mi Suscripción', url: '/intranet/my-subscription' }
];

const MySubscription = () => {
    const { auth } = useContext(AuthContext);
    const history = useHistory();
    const location = useLocation();
    
    const { 
        getPublicPlans,
        fetchingPublicPlans,
        getMyMembership,
        fetchingMyMembership,
        createSubscription,
        cancelSubscription,
        fetchingPurchase
    } = useMembershipPlanService();
    
    const [plans, setPlans] = useState<MembershipPlan[]>([]);
    const [myMembership, setMyMembership] = useState<UserMembership | null>(null);

    useEffect(() => {
        // Manejar parámetros de retorno de Flow
        const params = new URLSearchParams(location.search);
        const success = params.get('success');
        const error = params.get('error');
        
        if (success === 'subscribed') {
            toast.success('¡Suscripción activada exitosamente! Se cobrará automáticamente cada mes.');
            history.replace('/intranet/my-subscription');
        } else if (error) {
            const errorMessages: Record<string, string> = {
                'no_token': 'Error en el proceso de registro. Por favor intente nuevamente.',
                'verification_failed': 'No se pudo verificar el registro de la tarjeta.',
                'card_rejected': 'El registro de la tarjeta fue rechazado. Intente con otra tarjeta.',
                'no_customer': 'Error al identificar el cliente. Por favor intente nuevamente.',
                'no_pending': 'No se encontró una suscripción pendiente. Por favor intente nuevamente.',
                'subscription_failed': 'Error al crear la suscripción. Por favor intente nuevamente.',
                'membership_not_found': 'No se encontró la membresía asociada al pago.',
                'payment_failed': 'El pago fue rechazado. Intente con otro medio de pago.',
                'payment_cancelled': 'El pago fue cancelado.',
                'payment_pending': 'El pago quedó pendiente de confirmación. Te avisaremos cuando se acredite.',
                'unknown': 'Ocurrió un error inesperado. Por favor intente nuevamente.'
            };
            toast.error(errorMessages[error] || 'Error en el proceso de suscripción.');
            history.replace('/intranet/my-subscription');
        }
        
        loadData();
    }, []);

    const loadData = () => {
        // Cargar planes disponibles
        getPublicPlans({
            onSuccess: (res: any) => {
                setPlans(res.data?.plans || []);
            },
            onError: (res: any) => {
                toast.error('Error al cargar planes');
            }
        });

        // Cargar mi membresía actual
        getMyMembership({
            onSuccess: (res: any) => {
                if (res.data?.membership) {
                    setMyMembership(res.data.membership);
                } else {
                    setMyMembership(null);
                }
            },
            onError: () => {
                setMyMembership(null);
            }
        });
    };

    const handleSubscribe = (planId: number) => {
        createSubscription(planId, {
            onSuccess: (res: any) => {
                if (res.data?.needs_card_registration && res.data?.registration_url) {
                    toast.info('Serás redirigido a Flow para registrar tu tarjeta...');
                    window.location.href = safeRedirectUrl(res.data.registration_url);
                } else if (res.data?.needs_card_registration === false) {
                    toast.success(res.message || '¡Suscripción activada exitosamente!');
                    loadData();
                } else if (res.data?.redirect_url) {
                    window.location.href = safeRedirectUrl(res.data.redirect_url);
                } else if (res.data?.payment_url) {
                    window.location.href = safeRedirectUrl(res.data.payment_url);
                } else {
                    toast.success(res.message || '¡Suscripción activada!');
                    loadData();
                }
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al procesar suscripción');
            }
        });
    };

    const handleCancelSubscription = () => {
        if (!window.confirm('¿Estás seguro de cancelar tu suscripción? Podrás seguir usando el plan hasta el fin del período actual.')) {
            return;
        }
        
        cancelSubscription(true, {
            onSuccess: (res: any) => {
                toast.success(res.message || 'Suscripción cancelada');
                loadData();
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cancelar');
            }
        });
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Helpers para mostrar valores ilimitados
    const getContractsDisplay = (membership: UserMembership) => {
        const used = membership.contracts_used;
        const limit = membership.plan.contracts_included;
        if (limit === -1 || membership.plan.is_unlimited_contracts) {
            return `${used} / ∞`;
        }
        return `${used} / ${limit}`;
    };

    const getChatbotDisplay = (membership: UserMembership) => {
        const used = membership.chatbot_messages_used;
        const limit = membership.plan.chatbot_messages_limit;
        if (limit === -1 || membership.plan.is_chatbot_unlimited) {
            return `${used} / ∞`;
        }
        return `${used} / ${limit}`;
    };

    const getPlanChatbotDisplay = (plan: MembershipPlan) => {
        if (plan.chatbot_messages_limit === -1 || plan.is_chatbot_unlimited) {
            return 'Chatbot ilimitado';
        }
        return `${plan.chatbot_messages_limit} mensajes`;
    };

    return (
        <div className="container-fluid">
            <PageHeader title="Mi Suscripción" breadcrumbs={breadcrumbs} />
            
            <div className="my-subscription-page">
                {/* Membresía Actual */}
                {fetchingMyMembership ? (
                    <div className="loading-card">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                ) : myMembership && myMembership.is_valid ? (
                    <div className="current-membership">
                        <div className="membership-header">
                            <div className="membership-icon">✅</div>
                            <div className="membership-info">
                                <h2>Plan Activo</h2>
                                <p className="plan-name">{myMembership.plan.name}</p>
                            </div>
                            {myMembership.is_subscription && (
                                <span className="subscription-badge">Suscripción activa</span>
                            )}
                        </div>
                        
                        <div className="membership-details">
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <span className="label">Válido hasta</span>
                                    <span className="value">{formatDate(myMembership.end_date)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Días restantes</span>
                                    <span className="value">{myMembership.days_remaining} días</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Contratos usados</span>
                                    <span className="value">{getContractsDisplay(myMembership)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Mensajes chatbot</span>
                                    <span className="value">{getChatbotDisplay(myMembership)}</span>
                                </div>
                            </div>
                            
                            {myMembership.is_subscription && myMembership.next_invoice_date && (
                                <div className="next-payment">
                                    <span className="label">Próximo cobro:</span>
                                    <span className="value">{formatDate(myMembership.next_invoice_date)}</span>
                                </div>
                            )}
                            
                            {myMembership.is_subscription && myMembership.subscription_status === 'active' && (
                                <div className="subscription-actions mt-3">
                                    <button 
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={handleCancelSubscription}
                                        disabled={fetchingPurchase}
                                    >
                                        Cancelar suscripción
                                    </button>
                                    <small className="text-muted d-block mt-1">
                                        Al cancelar, podrás seguir usando el plan hasta el fin del período actual.
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="no-membership">
                        <div className="no-membership-icon"><i className="fas fa-file-signature" /></div>
                        <h2>No tienes un plan activo</h2>
                        <p>Contrata nuestro plan para acceder a contratos ilimitados y chatbot legal 24/7</p>
                    </div>
                )}

                {/* Planes Disponibles */}
                <div className="plans-section">
                    <h2 className="section-title">
                        {myMembership?.is_valid ? 'Tu plan actual' : 'Planes disponibles'}
                    </h2>
                    
                    {fetchingPublicPlans ? (
                        <div className="loading-plans">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="plans-grid">
                            {plans.map(plan => (
                                <div 
                                    key={plan.id} 
                                    className={`plan-card ${plan.is_featured ? 'featured' : ''} ${myMembership?.plan?.id === plan.id ? 'current' : ''}`}
                                >
                                    {plan.badge_text && (
                                        <div className="plan-badge">{plan.badge_text}</div>
                                    )}
                                    
                                    <div className="plan-header">
                                        <h3 className="plan-name">{plan.name}</h3>
                                        <p className="plan-short-desc">{plan.short_description}</p>
                                    </div>
                                    
                                    <div className="plan-price">
                                        <span className="price">{plan.price_formatted}</span>
                                        <span className="iva">+ IVA</span>
                                        <span className="period">/ {plan.duration_display}</span>
                                    </div>
                                    
                                    <div className="plan-total">
                                        Total: <strong>{plan.price_with_iva_formatted}</strong>
                                    </div>
                                    
                                    <div className="plan-description">
                                        <p>{plan.description}</p>
                                    </div>
                                    
                                    <ul className="plan-features">
                                        {plan.features && plan.features.map((feature, idx) => (
                                            <li key={idx}>
                                                <span className="check-icon">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    {plan.exclusions && plan.exclusions.length > 0 && (
                                        <ul className="plan-exclusions">
                                            {plan.exclusions.map((exclusion, idx) => (
                                                <li key={idx}>
                                                    <span className="x-icon">✗</span>
                                                    {exclusion}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    
                                    <div className="plan-highlights">
                                        <div className="highlight">
                                            <span className="icon">📝</span>
                                            <span className="text">{plan.contracts_display}</span>
                                        </div>
                                        {plan.chatbot_enabled && (
                                            <div className="highlight">
                                                <span className="icon">🤖</span>
                                                <span className="text">{getPlanChatbotDisplay(plan)}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {myMembership?.plan?.id === plan.id ? (
                                        <button className="plan-button current" disabled>
                                            Plan actual
                                        </button>
                                    ) : (
                                        <button 
                                            className="plan-button"
                                            onClick={() => handleSubscribe(plan.id)}
                                            disabled={fetchingPurchase}
                                        >
                                            {fetchingPurchase ? 'Procesando...' : 'Suscribirse ahora'}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Beneficios */}
                <div className="benefits-section">
                    <h2 className="section-title">¿Por qué contratar?</h2>
                    <div className="benefits-grid">
                        <div className="benefit-card">
                            <div className="benefit-icon">⚡</div>
                            <h4>Sin límites</h4>
                            <p>Genera todos los contratos que necesites, sin restricciones mensuales.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="benefit-icon">🤖</div>
                            <h4>IA Legal 24/7</h4>
                            <p>Consulta dudas legales cuando quieras con nuestro chatbot especializado.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="benefit-icon">💰</div>
                            <h4>Ahorra dinero</h4>
                            <p>Un solo pago mensual en lugar de pagar por cada contrato individual.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="benefit-icon">🇨🇱</div>
                            <h4>Normativa chilena</h4>
                            <p>Todos los documentos están adaptados a la legislación vigente en Chile.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MySubscription;
